import React, { useEffect, useState } from "react";
import { Dropdown, Alert, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  ETGetUsers,
  ETAddUser,
  ETUpdateUser,
  ETUserDelete,
} from "../../../../APIS";
import { emojis } from "../../bootstrap/alertData";
import { useParams } from "react-router-dom";

const ChairLiftUsers = () => {
  const { serviceId } = useParams();
  const [usersData, setUsersData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // Alerts
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Form visibility + mode
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    roles: "user",
    Service: "",
    City: "",
    service_id: serviceId,
  });

  const itemsPerPage = 10;

  // ✅ Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await ETGetUsers(serviceId);
      setUsersData(res.data.users || []);
    } catch (error) {
      setErrorMsg("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [serviceId]);

  // ✅ Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ Submit (Add/Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await ETUpdateUser(formData.id, formData);
        setSuccessMsg("User updated successfully ✅");
      } else {
        await ETAddUser(formData);
        setSuccessMsg("User added successfully ✅");
      }
      setShowForm(false);
      setIsEditMode(false);
      fetchUsers();
    } catch (error) {
      setErrorMsg("Error saving user ❌");
    }
  };

  // ✅ Delete user
  const handleDelete = async (id) => {
    try {
      await ETUserDelete(id);
      setSuccessMsg("User deleted successfully ✅");
      fetchUsers();
    } catch (error) {
      setErrorMsg("Error deleting user ❌");
    }
  };

  // ✅ Filter + paginate
  const filteredUsers = usersData.filter((user) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(lowerSearch) ||
      user.email?.toLowerCase().includes(lowerSearch) ||
      user.roles?.toLowerCase().includes(lowerSearch)
    );
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="col-12">
      {/* Alerts */}
      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
        >
          {emojis.success} {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setErrorMsg(null)}
        >
          {emojis.warning} {errorMsg}
        </Alert>
      )}

      <div className="card">
        {/* Show Table OR Form */}
        {!showForm ? (
          <>
            {/* Header */}
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title m-0">Users</h4>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email or role"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                />
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setFormData({
                      name: "",
                      email: "",
                      password: "",
                      roles: "user",
                      Service: "",
                      City: "",
                      service_id: serviceId,
                    });

                    setIsEditMode(false);
                    setShowForm(true);
                  }}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Add User
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center p-4">
                  <Spinner animation="border" role="status" />
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Service</th>
                        <th>Role</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length > 0 ? (
                        paginatedUsers.map((user, i) => (
                          <tr key={user.id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.Service || "--"}</td>
                            <td>{user.roles}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  &#x2022;&#x2022;&#x2022;
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item
                                    onClick={() => {
                                      setFormData(user);
                                      setIsEditMode(true);
                                      setShowForm(true);
                                    }}
                                  >
                                    Edit User
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(user.id)}
                                  >
                                    Delete User
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing{" "}
                  {filteredUsers.length === 0
                    ? 0
                    : currentPage * itemsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * itemsPerPage,
                    filteredUsers.length
                  )}{" "}
                  of {filteredUsers.length} entries
                </div>
                <div>
                  <Link
                    className={`btn btn-sm btn-outline-primary me-2 ${
                      currentPage === 0 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage > 0 && setCurrentPage(currentPage - 1)
                    }
                  >
                    Prev
                  </Link>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Link
                      key={i}
                      className={`btn btn-sm border me-2 ${
                        currentPage === i ? "btn-primary text-white" : ""
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </Link>
                  ))}
                  <Link
                    className={`btn btn-sm btn-outline-primary ms-2 ${
                      currentPage === totalPages - 1 ? "disabled" : ""
                    }`}
                    onClick={() =>
                      currentPage + 1 < totalPages &&
                      setCurrentPage(currentPage + 1)
                    }
                  >
                    Next
                  </Link>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Add/Edit Form */
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Name<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter name..."
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Email</label>
                    <div className="col-lg-6">
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter email..."
                        required
                      />
                    </div>
                  </div>
                </div>

                {!isEditMode && (
                  <div className="col-xl-6">
                    <div className="mb-3 row">
                      <label className="col-lg-4 col-form-label">
                        Password
                      </label>
                      <div className="col-lg-6">
                        <input
                          type="password"
                          name="password"
                          value={formData.password || ""}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter password..."
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ✅ City field in form */}
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">City</label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="City"
                        value={formData.City || ""}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter city..."
                      />
                    </div>
                  </div>
                </div>

                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">Role</label>
                    <div className="col-lg-6">
                      <select
                        name="roles"
                        value={formData.roles}
                        onChange={handleChange}
                        className="form-control"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="supervisor">Supervisor</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary">
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ms-2"
                      onClick={() => {
                        setShowForm(false);
                        setIsEditMode(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChairLiftUsers;
